import React, { useState, useEffect, useRef } from "react";
import {
  ArrowLeft,
  Target,
  Globe,
  Smartphone,
  CheckCircle2,
  Briefcase,
  GraduationCap,
  ArrowRight,
  Heart,
  Building2,
  Award,
  Users,
  Cpu,
  Rocket,
  ShieldCheck,
  MapPin,
  Mail,
  Linkedin,
  ExternalLink,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const CountUp = ({ end, duration = 2000, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const elementRef = useRef(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          let startTimestamp = null;
          const step = (timestamp) => {
            if (!startTimestamp) startTimestamp = timestamp;
            const progress = Math.min(
              (timestamp - startTimestamp) / duration,
              1,
            );
            // Cubic ease out
            const easeProgress = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(easeProgress * end));

            if (progress < 1) {
              window.requestAnimationFrame(step);
            }
          };
          window.requestAnimationFrame(step);
        }
      },
      { threshold: 0.2 },
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => {
      if (elementRef.current) {
        observer.disconnect();
      }
    };
  }, [end, duration]);

  return (
    <span ref={elementRef}>
      {count.toLocaleString()}
      {suffix}
    </span>
  );
};

const AboutUsPage = () => {
  const onNavigate = useNavigate();
  return (
    <div className="bg-white min-h-screen font-sans text-slate-900 leading-relaxed">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-8 xl:px-6 xsm:px-4 h-16 flex items-center justify-between">
          <button
            onClick={() => window.history.back()}
            className="flex items-center gap-2 text-slate-600 hover:text-blue-600 font-bold text-sm transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="font-bold text-lg text-slate-900">About Us</div>
          <div className="w-10"></div>
        </div>
      </div>

      {/* Hero Section - Emotional Story */}
      <div className="bg-slate-900 py-32 xl:py-24 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 pointer-events-none"></div>
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-600 rounded-full blur-[120px] opacity-20 -mr-20 -mt-20 pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-purple-600 rounded-full blur-[120px] opacity-20 -ml-20 -mb-20 pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-8 xl:px-6 xsm:px-4 relative z-10 text-center">
          <span
          className="inline-flex items-center gap-2 py-1.5 px-4 rounded-full  text-xs font-bold mb-8 tracking-wide uppercase backdrop-blur-sm bg-gradient-to-r from-blue-400 to-purple-400 border  text-brand-300"
          // className="inline-flex items-center gap-2 py-1.5 px-4 rounded-full bg-white/10 border border-white/10 text-brand-300 text-xs font-bold mb-8 tracking-wide uppercase backdrop-blur-sm"
          >
            <Heart className="w-3 h-3 fill-current" /> Our Story
          </span>
          <h1 className="text-6xl xl:text-4xl font-black text-white tracking-tight mb-8 leading-tight">
            Democratizing Opportunities <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
              Beyond Metros
            </span>
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed font-medium">
            Talent is everywhere, but opportunity is not. We are on a mission to
            bridge this gap, serving the underserved heartlands of India. From
            the hills of the{" "}
            <span className="text-white font-bold">North East</span> to the
            vibrant towns of{" "}
            <span className="text-white font-bold">
              Odisha, Bihar, UP, and Chhattisgarh
            </span>
            , we are building digital highways that connect aspirations with
            achievements.
          </p>
        </div>
      </div>

      {/* Vision & Mission */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-8 xl:px-6 xsm:px-4">
          <div className="grid grid-cols-2 sm:grid-cols-1 gap-12 xl:gap-8">
            {/* Mission */}
            <div className="bg-slate-50 rounded-[2rem] p-10 border border-slate-100 hover:shadow-xl transition-shadow group">
              <div className="bg-blue-100 w-14 h-14 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Target className="text-blue-700 w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">
                Our Mission
              </h3>
              <p className="text-slate-600 leading-relaxed text-lg">
                To empower 10M+ graduates with meaningful internships and
                fresher jobs. We strive to bridge the gap between small
                companies and startups in Tier 2 & 3 cities and eager talent,
                creating an inclusive hiring ecosystem beyond the metros.
              </p>
            </div>

            {/* Vision */}
            <div className="bg-slate-50 rounded-[2rem] p-10 border border-slate-100 hover:shadow-xl transition-shadow group">
              <div className="bg-indigo-100 w-14 h-14 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Globe className="text-indigo-600 w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">
                Our Vision
              </h3>
              <p className="text-slate-600 leading-relaxed text-lg">
                To become the definitive operating system for early-career
                employment in India. We envision a future where every student,
                regardless of their college or location, has access to
                meaningful work.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Our Platforms */}
      <div className="py-24 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-8 xl:px-6 xsm:px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl xl:text-3xl font-black text-slate-900 mb-4">
              Our Ecosystem
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Three powerful platforms, one unified mission to transform the
              education-to-employment journey.
            </p>
          </div>

          <div className="grid grid-cols-3 sm:grid-cols-1 gap-8">
            {/* 1. Freshment (Students) */}
            <div className="bg-white rounded-[2rem] shadow-sm border border-slate-200 overflow-hidden flex flex-col hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 group">
              <div className="p-8 flex-grow">
                <div className="w-14 h-14 bg-indigo-50 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Smartphone className="text-indigo-600 w-7 h-7" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-1">
                  Freshment
                </h3>
                <div className="inline-block text-[10px] bg-indigo-50 text-indigo-700 font-bold uppercase tracking-wider px-2 py-1 rounded mb-4">
                  For Job Seekers
                </div>

                <p className="text-slate-600 mb-6 leading-relaxed">
                  India's fastest-growing career discovery platform. We help
                  students from Tier-2/3 cities build professional profiles and
                  land their first job.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center text-sm text-slate-500 font-medium">
                    <CheckCircle2 className="text-emerald-500 mr-3 w-5 h-5 flex-shrink-0" />{" "}
                    Find your First Job
                  </li>
                  <li className="flex items-center text-sm text-slate-500 font-medium">
                    <CheckCircle2 className="text-emerald-500 mr-3 w-5 h-5 flex-shrink-0" />{" "}
                    Verified Job Listings
                  </li>
                  <li className="flex items-center text-sm text-slate-500 font-medium">
                    <CheckCircle2 className="text-emerald-500 mr-3 w-5 h-5 flex-shrink-0" />{" "}
                    Interview Papers
                  </li>
                </ul>
              </div>
              <div className="p-6 border-t border-slate-100 bg-slate-50/50">
                <button
                  onClick={() => onNavigate("/freshment-app")}
                  className="w-full py-3 rounded-xl border border-indigo-200 text-indigo-700 font-bold text-sm flex items-center justify-center gap-2 hover:bg-indigo-50 transition-all"
                >
                  Explore App <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* 2. Talent Connect (Employers) */}
            <div className="bg-white rounded-[2rem] shadow-sm border border-slate-200 overflow-hidden flex flex-col hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 group">
              <div className="p-8 flex-grow">
                <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Briefcase className="text-blue-700 w-7 h-7" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-1">
                  Talent Connect
                </h3>
                <div className="inline-block text-[10px] bg-blue-50 text-blue-700 font-bold uppercase tracking-wider px-2 py-1 rounded mb-4">
                  For Employers
                </div>

                <p className="text-slate-600 mb-6 leading-relaxed">
                  The growth engine for Startups & MSMEs. Scale your team fast
                  with instant access to pre-vetted campus talent from across
                  India.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center text-sm text-slate-500 font-medium">
                    <CheckCircle2 className="text-emerald-500 mr-3 w-5 h-5 flex-shrink-0" />{" "}
                    2 Lakh+ Verified Resumes
                  </li>
                  <li className="flex items-center text-sm text-slate-500 font-medium">
                    <CheckCircle2 className="text-emerald-500 mr-3 w-5 h-5 flex-shrink-0" />{" "}
                    Direct Campus Access
                  </li>
                  <li className="flex items-center text-sm text-slate-500 font-medium">
                    <CheckCircle2 className="text-emerald-500 mr-3 w-5 h-5 flex-shrink-0" />{" "}
                    Zero-Commission Hiring
                  </li>
                </ul>
              </div>
              <div className="p-6 border-t border-slate-100 bg-slate-50/50">
                <button
                  onClick={() => onNavigate("/talent-connect")}
                  className="w-full py-3 rounded-xl border border-blue-200 text-blue-700 font-bold text-sm flex items-center justify-center gap-2 hover:bg-blue-50 transition-all"
                >
                  Learn More <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* 3. EduConnectPro (Colleges) - NEW */}
            <div className="bg-white rounded-[2rem] shadow-sm border border-slate-200 overflow-hidden flex flex-col hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 group">
              <div className="p-8 flex-grow">
                <div className="w-14 h-14 bg-orange-50 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <GraduationCap className="text-[#FF5F14] w-7 h-7" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-1">
                  EduConnectPro
                </h3>
                <div className="inline-block text-[10px] bg-orange-50 text-[#FF5F14] font-bold uppercase tracking-wider px-2 py-1 rounded mb-4">
                  For Colleges
                </div>

                <p className="text-slate-600 mb-6 leading-relaxed">
                  Empowering placement cells with next-gen digital tools.
                  Automate drives, track offers, and connect students with top
                  companies.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center text-sm text-slate-500 font-medium">
                    <CheckCircle2 className="text-emerald-500 mr-3 w-5 h-5 flex-shrink-0" />{" "}
                    Digital Placement Cell
                  </li>
                  <li className="flex items-center text-sm text-slate-500 font-medium">
                    <CheckCircle2 className="text-emerald-500 mr-3 w-5 h-5 flex-shrink-0" />{" "}
                    300+ Monthly Leads
                  </li>
                  <li className="flex items-center text-sm text-slate-500 font-medium">
                    <CheckCircle2 className="text-emerald-500 mr-3 w-5 h-5 flex-shrink-0" />{" "}
                    Real-time Reporting
                  </li>
                </ul>
              </div>
              <div className="p-6 border-t border-slate-100 bg-slate-50/50">
                <button
                  onClick={() => onNavigate("/edu-connect")}
                  className="w-full py-3 rounded-xl border border-orange-200 text-[#FF5F14] font-bold text-sm flex items-center justify-center gap-2 hover:bg-orange-50 transition-all"
                >
                  View Solutions <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* Our Impact Highlights */}
      <div className="py-24 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-8 xl:px-6 xsm:px-4">
          <div className="text-center mb-16">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-violet-50 text-violet-700 text-xs font-black uppercase tracking-wider rounded-full mb-3">
              <Award className="w-3.5 h-3.5" /> Purpose & Direction
            </span>
            <h2 className="text-4xl xl:text-3xl font-black text-slate-900 tracking-tight">Impact Highlights</h2>
            <p className="text-slate-500 mt-2 max-w-2xl mx-auto font-medium">
              Transforming early careers and bridging structural divides to build India's future workforce.
            </p>
          </div>

          <div className="grid grid-cols-3 xl:grid-cols-2 xsm:grid-cols-1 gap-8">
            {[
              { title: 'Transforming Education into Livelihood Opportunities', desc: 'Transforming classroom training directly into sustainable livelihood opportunities.', icon: GraduationCap, color: 'text-brand-700 bg-brand-50 border-brand-100' },
              { title: 'Connecting Students, Colleges, Startups & Employers', desc: 'Unifying Students, Colleges, high-growth Startups and MSME Employers.', icon: Users, color: 'text-indigo-600 bg-indigo-50 border-indigo-100' },
              { title: 'AI-Powered Career Pathways & Job Matching', desc: 'Deploying highly curated Career Pathways and intelligent candidate-to-job matching.', icon: Cpu, color: 'text-violet-600 bg-violet-50 border-violet-100' },
              { title: 'Affordable Hiring Solutions for Startups & MSMEs', desc: 'Delivering robust, zero-friction and highly affordable placement solutions for startups & MSMEs.', icon: Briefcase, color: 'text-orange-600 bg-orange-50 border-orange-100' },
              { title: 'Building India\'s Future Workforce Ecosystem', desc: 'Paving the way for India\'s dynamic future technical and management workforce.', icon: Rocket, color: 'text-rose-600 bg-rose-50 border-rose-100' },
              { title: 'Creating Equal Access to Opportunities for Underserved Students', desc: 'Pioneering democratic, transparent opportunities for underserved Tier-2 and Tier-3 graduates.', icon: Globe, color: 'text-cyan-600 bg-cyan-50 border-cyan-100' }
            ].map((highlight, idx) => {
              const Icon = highlight.icon;
              return (
                <div key={idx} className="bg-slate-50 border border-slate-200/50 rounded-[2rem] shadow-sm p-8 transition hover:shadow-lg duration-300">
                  <div className={`p-3 rounded-xl border w-fit mb-6 ${highlight.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <h4 className="text-base font-black text-slate-900 tracking-tight mb-2">{highlight.title}</h4>
                  <p className="text-xs font-semibold text-slate-400 leading-relaxed">{highlight.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Leadership & Trusted Corporate Verification Section */}
      <div className="py-24 bg-white border-t border-slate-100 overflow-x-hidden">
        <div className="max-w-7xl mx-auto px-8 xl:px-6 xsm:px-4">

          <div className="text-center mb-16">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-brand-50 text-brand-700 text-xs font-black uppercase tracking-wider rounded-full mb-3">
              <ShieldCheck className="w-3.5 h-3.5" /> Compliance & Startup Verification
            </span>
            <h2 className="text-4xl xl:text-3xl font-black text-slate-900 tracking-tight">Leadership & Corporate Registry</h2>
            <p className="text-slate-500 mt-2 max-w-2xl mx-auto font-medium">
              Freshment is a fully structured, operational startup directed under the highest compliance and governance standards in India.
            </p>
          </div>

          <div className="grid grid-cols-12 gap-8 items-start sm:grid-cols-1">

            {/* Founder Spotlight Card */}
            <div className="col-span-7  sm:col-span-12 bg-slate-900 text-white rounded-[2.5rem] p-14 xl:p-10 sm:p-8 xsm:px-4 relative overflow-hidden shadow-xl shadow-slate-950/20 mx-auto max-w-full xsm:max-w-[320px]">
              <div className="absolute top-0 right-0 w-80 h-80 bg-brand-500/10 rounded-full blur-[80px] pointer-events-none"></div>

              <div className="relative z-10 space-y-6">
                <div>
                  <span className="text-[10px] bg-brand-500/25 border border-brand-500/30 text-brand-300 font-extrabold uppercase tracking-widest px-2.5 py-1.5 rounded-md">
                    FOUNDER & EXECUTIVE DIRECTOR
                  </span>
                  <h3 className="text-4xl xl:text-3xl font-black tracking-tight mt-3 text-white">Tejal Sonaiya</h3>
                  <p className="text-brand-300 font-bold text-sm mt-1">Founder & CEO, GRADCONNECT INNOVATIONS PRIVATE LIMITED</p>
                </div>

                <p className="text-slate-300 leading-relaxed text-base xl:text-sm font-medium">
                  Under the governance and strategic leadership of Tejal Sonaiya, GRADCONNECT INNOVATIONS PRIVATE LIMITED is committed to democratizing educational placement gateways. Tejal drives platform compliance, national startup strategies (such as the Startup Odisha Drive), and enterprise relations to provide a reliable career entry environment for 10M+ candidates across underserved regional college sectors.
                </p>

                <div className="pt-4 border-t border-slate-800">
                  <a
                    href="https://www.linkedin.com/in/tejal-s-9749641b4/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-500 active:scale-95 text-white font-extrabold px-6 py-3.5 rounded-xl text-xs tracking-wider uppercase transition-all shadow-lg shadow-brand-500/20"
                  >
                    <Linkedin className="w-4 h-4 fill-current" />
                    Verify LinkedIn Profile
                    <ExternalLink className="w-3.5 h-3.5 text-white/70" />
                  </a>
                </div>
              </div>
            </div>

            {/* Corporate Entity Details */}
            <div className="col-span-5 sm:col-span-12 space-y-6">

              <div className="bg-slate-50 border border-slate-200/60 rounded-[2rem] p-8 space-y-6">
                <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest">Freshment Corporate Registry</h4>

                <div className="space-y-5">
                  <div className="flex items-start gap-3.5">
                    <div className="p-2.5 bg-brand-50 text-brand-600 rounded-xl border border-brand-100">
                      <Building2 className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Registered Entity Name</div>
                      <div className="text-sm font-black text-slate-900 mt-0.5">GRADCONNECT INNOVATIONS PRIVATE LIMITED</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3.5">
                    <div className="p-2.5 bg-brand-50 text-brand-600 rounded-xl border border-brand-100">
                      <MapPin className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">CORPORATE ADDRESS</div>
                      <address className="text-xs font-bold text-slate-600 mt-0.5 not-italic leading-relaxed">
                        B-519, RK Iconic Sheetal Park, Puneet Naga Bajrang,<br />
                        Rajkot Bhomeshwar Plot, Rajkot- 360006, Gujarat
                      </address>
                    </div>
                  </div>

                  <div className="flex items-start gap-3.5">
                    <div className="p-2.5 bg-brand-50 text-brand-600 rounded-xl border border-brand-100">
                      <Mail className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">OFFICIAL ELECTRONIC CONTACT</div>
                      <div className="text-xs font-bold text-slate-600 mt-0.5">
                        <a href="mailto:contact@freshmentapp.com" className="text-brand-600 hover:underline">contact@freshmentapp.com</a>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pt-5 border-t border-slate-200/80 flex items-center gap-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  <ShieldCheck className="w-4 h-4 text-emerald-500 shrink-0" /> Status: Active Compliant Startup
                </div>
              </div>

              {/* Support Notification Banner */}
              <div className="p-6 bg-slate-50 border border-slate-200/60 rounded-[1.5rem] text-left space-y-2">
                <h4 className="text-xs font-black text-slate-900 tracking-wider uppercase flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-brand-600 rounded-full animate-pulse"></span>
                  Ecosystem Verification Channel
                </h4>
                <p className="text-[11px] leading-relaxed text-slate-500 font-medium">
                  We are fully operational and verified under state/national incubator ecosystems. If you require further legal compliance metrics or validation details, please connect with our Corporate Coordinator group.
                </p>
                <a
                  href="mailto:contact@freshmentapp.com"
                  className="inline-flex items-center gap-1 text-[11px] font-bold text-brand-600 hover:text-brand-700 transition"
                >
                  Reach Corporate Coordinator &rarr;
                </a>
              </div>

            </div>

          </div>

        </div>
      </div>




      {/* Impact Stats */}
      <div className="bg-slate-900 py-20 text-white border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-8 xl:px-6 xsm:px-4">
          <div className="grid grid-cols-4 sm:grid-cols-2 xsm:grid-cols-1 gap-8 text-center">
            <div className="p-4">
              <div className="text-5xl sm:text-4xl font-black text-white mb-2">
                <CountUp end={100000} suffix="+" />
              </div>
              <div className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                App Downloads
              </div>
            </div>
            <div className="p-4">
              <div className="text-5xl sm:text-4xl font-black text-white mb-2">
                <CountUp end={200000} suffix="+" />
              </div>
              <div className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                Students Connected
              </div>
            </div>
            <div className="p-4">
              <div className="text-5xl sm:text-4xl font-black text-white mb-2">
                <CountUp end={3000} suffix="+" />
              </div>
              <div className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                Colleges Network Reach
              </div>
            </div>
            <div className="p-4">
              <div className="text-5xl sm:text-4xl font-black text-white mb-2">
                <CountUp end={25000} suffix="+" />
              </div>
              <div className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                Job Applications Facilitated
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dual CTA Section */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-8 xl:px-6 xsm:px-4">
          <div className="grid grid-cols-2 sm:grid-cols-1 gap-12 xl:gap-8">
            {/* Employer CTA */}
            <div className="bg-blue-50 rounded-[2.5rem] p-14 sm:p-10 text-center border border-blue-100 flex flex-col items-center">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-6 shadow-sm text-blue-600">
                <Building2 className="w-8 h-8" />
              </div>
              <h2 className="text-3xl sm:text-2xl font-black text-slate-900 mb-4">
                Ready to transform your hiring?
              </h2>
              <p className="text-slate-600 mb-8 max-w-md mx-auto leading-relaxed">
                Join thousands of startups and companies hiring pre-verified
                freshers from Tier-2 & 3 cities.
              </p>
              <button
                onClick={() => onNavigate("/talent-connect")}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-10 rounded-xl shadow-lg shadow-blue-500/20 transition-transform hover:-translate-y-1 w-auto xsm:w-full"
              >
                Hire Talent Now
              </button>
            </div>

            {/* College CTA */}
            <div className="bg-orange-50 rounded-[2.5rem] p-14 sm:p-10 text-center border border-orange-100 flex flex-col items-center">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-6 shadow-sm text-[#FF5F14]">
                <GraduationCap className="w-8 h-8" />
              </div>
              <h2 className="text-3xl sm:text-2xl font-black text-slate-900 mb-4">
                Digitize your Placement Cell?
              </h2>
              <p className="text-slate-600 mb-8 max-w-md mx-auto leading-relaxed">
                Empower your students with better opportunities and automate
                your placement workflow today.
              </p>
              <button
                onClick={() => onNavigate("/edu-connect")}
                className="bg-[#FF5F14] hover:bg-[#e04f0d] text-white font-bold py-4 px-10 rounded-xl shadow-lg shadow-orange-500/20 transition-transform hover:-translate-y-1 w-auto xsm:w-full"
              >
                Partner with Us
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutUsPage;
